import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { StockBasicInfoResponse } from '../../../server/response/responseSchema';
import { apiClient } from '../../clientUtils/apiClient';
import { useMobxInstance } from '../../clientUtils/mobxUtil';

class ViewStore {
    ticker: string;
    stockBasicInfo: StockBasicInfoResponse | null = null;
    isLoading: boolean = true;
    error: string | null = null;

    constructor(ticker: string) {
        this.ticker = ticker;
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;
        this.error = null;

        try {
            const stockBasicInfo = await apiClient.stock.public.getStockBasicInfo.$query({
                ticker: this.ticker,
            });

            runInAction(() => {
                this.stockBasicInfo = stockBasicInfo;
            });
        } catch (error) {
            console.error('주식 기본 정보 조회 실패:', error);
            runInAction(() => {
                this.error = '주식 정보를 불러오는데 실패했습니다.';
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }
}

type StockBasicInfoProps = {
    ticker: string;
};

const StockBasicInfo = observer(({ ticker }: StockBasicInfoProps) => {
    const viewStore = useMobxInstance(ViewStore, ticker);

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    if (!viewStore.isLoading && !viewStore.stockBasicInfo) {
        return (
            <div className="py-8 text-center">
                <p>종목 정보를 찾을 수 없습니다.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {/* 종목명과 핵심 정보 */}
            <div>
                <div className="text-base-content mb-1 h-6 text-lg font-bold">
                    {viewStore.isLoading ? (
                        <div className="skeleton h-full w-40 rounded-sm"></div>
                    ) : (
                        viewStore.stockBasicInfo?.nameKo || viewStore.stockBasicInfo?.name
                    )}
                </div>
                <div className="mb-3 flex h-6 items-center gap-3">
                    {viewStore.isLoading ? (
                        <div className="skeleton h-5 w-2/3 rounded-sm"></div>
                    ) : (
                        <>
                            <span className="text-base-content text-base font-semibold">
                                {viewStore.stockBasicInfo?.ticker}
                            </span>
                            {viewStore.stockBasicInfo?.nameKo && (
                                <span className="text-base-content/60 text-sm">
                                    {viewStore.stockBasicInfo?.name}
                                </span>
                            )}
                        </>
                    )}
                </div>
                {/* 모든 특징을 통합한 라벨 */}
                <div className="flex h-6 flex-wrap gap-1">
                    {viewStore.isLoading ? (
                        <div className="skeleton h-5 w-1/2 rounded-sm"></div>
                    ) : (
                        <>
                            {viewStore.stockBasicInfo?.sector && (
                                <div className="badge badge-outline badge-sm border-base-content/60 rounded-sm">
                                    {viewStore.stockBasicInfo?.sector.nameKo} 섹터
                                </div>
                            )}
                            {viewStore.stockBasicInfo?.isSp500 && (
                                <div className="badge badge-outline badge-sm rounded-sm">
                                    S&P500
                                </div>
                            )}
                            {viewStore.stockBasicInfo?.isNasdaq100 && (
                                <div className="badge badge-outline badge-sm rounded-sm">
                                    나스닥100
                                </div>
                            )}
                            {viewStore.stockBasicInfo?.isDow30 && (
                                <div className="badge badge-outline badge-sm rounded-sm">다우</div>
                            )}
                        </>
                    )}
                </div>
            </div>
            {/* 회사 소개 - 구분선으로 분리 */}
            {viewStore.isLoading ? (
                <div className="space-y-2">
                    <div className="skeleton h-5 w-full rounded-sm"></div>
                    <div className="skeleton h-5 w-full rounded-sm"></div>
                </div>
            ) : (
                <>
                    {viewStore.stockBasicInfo?.description && (
                        <div className="text-base-content/80 text-base">
                            {viewStore.stockBasicInfo?.description}
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

export default StockBasicInfo;
